import React, { useState, useEffect } from 'react';
import API from '../api/axios';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ProjectSearchSelect from '../components/ProjectSearchSelect';
import PageLoader from '../components/PageLoader';
import {
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiX, FiFileText, FiDownload, FiPrinter,
} from 'react-icons/fi';
import './QuotationMgmt.css'; // reuse the existing quo-* styles
import './CustomerOrders.css'; // reuse co-* form styles for the modal

const PAGE_SIZE = 8;

const STATUS_CFG = {
  draft:    { label: 'Draft',    color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
  sent:     { label: 'Sent',     color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span className="quo-badge" style={{ color: c.color, background: c.bg, border: `1px solid ${c.border}` }}>{c.label}</span>;
}

function ConfirmDialog({ title, message, confirmLabel = 'Confirm', confirmColor = '#2563eb', icon, onConfirm, onCancel }) {
  return (
    <div className="quo-confirm-overlay" onClick={onCancel}>
      <div className="quo-confirm-box" onClick={e => e.stopPropagation()}>
        {icon && <div className="quo-confirm-icon">{icon}</div>}
        <h4 className="quo-confirm-title">{title}</h4>
        <p className="quo-confirm-msg">{message}</p>
        <div className="quo-confirm-actions">
          <button className="quo-confirm-cancel" onClick={onCancel}>Cancel</button>
          <button className="quo-confirm-ok" style={{ background: confirmColor }} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
function fmt(iso) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
function fmtINR(n) { if (!n && n !== 0) return '—'; return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmt2(n) { return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function sectionArea(sec) {
  return (sec.rows || []).reduce((s, r) => s + ((+r.heightFt || 0) * (+r.widthFt || 0) * (+r.qty || 1)), 0);
}
function sectionAmount(sec) {
  return +(sectionArea(sec) * (+sec.rate || 0)).toFixed(2);
}
function quotationTotal(q) {
  return (q.sections || []).reduce((s, sec) => s + sectionAmount(sec), 0);
}

function freshRow() { return { heightFt: '', widthFt: '', qty: 1 }; }
function freshSection() { return { title: '', specifications: [''], rows: [freshRow()], rate: '' }; }

export default function CustomerQuotationMgmt() {
  const [list, setList]           = useState([]);
  const [selected, setSelected]   = useState(null);
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editQ, setEditQ]         = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, message, confirmLabel, confirmColor, icon, onConfirm }

  const load = async () => {
    try {
      setLoading(true);
      const r = await API.get('/customer-quotations');
      setList(r.data);
      setSelected(prev => prev ? (r.data.find(q => q._id === prev._id) || r.data[0] || null) : (r.data[0] || null));
    } catch { toast.error('Failed to load customer quotations'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = list.filter(q => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (q.quotationNumber || '').toLowerCase().includes(s) || (q.clientName || q.client?.name || '').toLowerCase().includes(s);
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  async function applyStatus(q, status) {
    try {
      const r = await API.put(`/customer-quotations/${q._id}/status`, { status });
      await load();
      setSelected(r.data);
      toast.success(`Marked ${status}`);
    } catch { toast.error('Failed to update status'); }
  }

  function updateStatus(q, status) {
    if (status === 'approved') {
      setConfirm({
        title: 'Approve Quotation',
        message: `Approve ${q.quotationNumber} as the final quotation for this project? Every other quotation on the same project will automatically be marked Rejected.`,
        confirmLabel: 'Approve',
        confirmColor: '#16a34a',
        icon: '⭐',
        onConfirm: () => { setConfirm(null); applyStatus(q, status); },
      });
      return;
    }
    applyStatus(q, status);
  }

  async function deleteQ(id) {
    try {
      await API.delete(`/customer-quotations/${id}`);
      setConfirmDel(null);
      const next = list.filter(q => q._id !== id);
      setList(next);
      setSelected(next[0] || null);
      toast.success('Quotation deleted');
    } catch { toast.error('Delete failed'); }
  }

  function exportPDF() {
    if (!selected) return;
    const q = selected;
    const win = window.open('', '_blank');
    const terms = (q.notes ? q.notes.split('\n').filter(Boolean) : ['This quotation is valid till the above mentioned date.'])
      .map(n => `<li>${n}</li>`).join('');

    const sectionsHtml = (q.sections || []).map((sec, si) => {
      const rowsHtml = (sec.rows || []).map(r => `
        <tr>
          <td class="c">${r.heightFt ?? '—'}</td>
          <td class="c">${r.widthFt ?? '—'}</td>
          <td class="c">${r.qty ?? 1}</td>
        </tr>`).join('');
      const area = sectionArea(sec);
      const amount = sectionAmount(sec);
      return `
        <div style="text-align:center;margin-top:10px"><span style="display:inline-block;background:#e2e8f0;font-weight:700;padding:3px 24px">${(sec.title || 'ESTIMATION').toUpperCase()}</span></div>
        <table class="main">
          <thead>
            <tr>
              <th rowspan="2" style="width:5%">S.NO</th>
              <th rowspan="2" style="width:35%">Descriptions</th>
              <th colspan="3">Dimensions</th>
              <th rowspan="2">Total Area in SQ/FT</th><th rowspan="2">Rate</th><th rowspan="2">Total Value In Rs.</th>
            </tr>
            <tr><th>Height in feet</th><th>Width in feet</th><th>Qty</th></tr>
          </thead>
          <tbody>
            <tr>
              <td class="c">${si + 1}</td>
              <td class="spec-cell">
                <b>SPECIFICATIONS:</b>
                <ol>${(sec.specifications || []).filter(Boolean).map(s => `<li>${s}</li>`).join('')}</ol>
              </td>
              <td colspan="3" class="dim-cell"><table class="dim-table">${rowsHtml}</table></td>
              <td class="c">${fmt2(area)}</td>
              <td class="c">₹${fmt2(sec.rate)}</td>
              <td class="c">₹${fmt2(amount)}</td>
            </tr>
            <tr class="total-row"><td colspan="7">TOTAL</td><td>₹${fmt2(amount)}</td></tr>
          </tbody>
        </table>`;
    }).join('');

    const grandTotal = quotationTotal(q);

    win.document.write(`<!DOCTYPE html><html><head><title>${q.quotationNumber}</title><style>
      body{font-family:Arial,sans-serif;padding:0;color:#000;font-size:12px}
      .sheet{border:2px solid #000;margin:16px;padding:14px}
      .top{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #000;padding-bottom:8px;margin-bottom:8px}
      .top-left{display:flex;gap:12px;align-items:flex-start}
      .top-left img{height:70px;width:auto;object-fit:contain}
      .co-name{font-size:22px;font-weight:800;color:#1e3a8a;letter-spacing:1px}
      .co-line{font-size:10.5px;color:#111;margin-top:2px}
      .co-contact{font-size:10.5px;margin-top:2px}
      .co-contact a{color:#1d4ed8}
      .offer-tbl{border-collapse:collapse;font-size:11px}
      .offer-tbl td{border:1px solid #000;padding:3px 8px}
      .offer-tbl td:first-child{font-weight:700;background:#f1f5f9}
      .to-block{margin:10px 0}
      .to-block p{margin:2px 0}
      .intro{font-size:11.5px;margin:10px 0}
      table.main{width:100%;border-collapse:collapse;margin-top:6px}
      table.main th,table.main td{border:1px solid #000;padding:5px 8px}
      table.main th{background:#f8fafc;font-size:10.5px;text-align:center}
      table.main td{font-size:11px;vertical-align:top}
      td.c{text-align:center}
      .dim-cell{padding:0!important}
      table.dim-table{width:100%;border-collapse:collapse}
      table.dim-table td{border:none;border-bottom:1px solid #000;padding:5px 8px;font-size:11px}
      table.dim-table tr:last-child td{border-bottom:none}
      .spec-cell{font-size:10.5px;line-height:1.6}
      .spec-cell ol{margin:0;padding-left:16px}
      .total-row td{font-weight:700;text-align:center;background:#f8fafc}
      .grand-total{text-align:right;font-weight:800;font-size:14px;margin-top:8px}
      .terms{margin-top:12px}
      .terms u{font-weight:700}
      .terms ol{margin:6px 0 0;padding-left:20px;font-size:11px;line-height:1.7}
      .bank{border:1px solid #000;width:340px;margin-top:10px}
      .bank .hd{text-align:center;font-weight:700;border-bottom:1px solid #000;padding:3px}
      .bank .sub{text-align:center;font-weight:700;color:#b91c1c;border-bottom:1px solid #000;padding:3px}
      .bank .row{padding:3px 8px;font-size:11px}
      .bank .row b{display:inline-block;width:70px}
      .sign{margin-top:14px;font-size:11.5px}
      .sign b{display:block;margin-top:6px}
      .sign .signatory{color:#b91c1c;font-weight:700;margin-top:22px}
      @media print{.sheet{margin:0;border:2px solid #000}}
    </style></head><body>
      <div class="sheet">
        <div class="top">
          <div class="top-left">
            <img src="${window.location.origin}/cmpy_logo.png" alt="Logo" onerror="this.style.display='none'" />
            <div>
              <div class="co-name">CREATIVE GLASS TEC</div>
              <div class="co-line">NO : 16, NEHRU STREET, SAKTHI VELLAMMAL NAGAR, NEAR IBACO ICE CREAM, BYEPASS ROAD, MADURAI</div>
              <div class="co-line">B.O: DR.AMBEDKAR NAGAR, SOUTH PERIYAR, VIRUDHACHALAM-606001</div>
              <div class="co-line">B.O: THANGA VEL MANIMOZHI VANIGA VALAGAM, THANJAVUR - 614904</div>
              <div class="co-contact">✉ Email: <a href="mailto:creativeglassstudio14@gmail.com">creativeglassstudio14@gmail.com</a></div>
              <div class="co-contact">☎ CONTACT NUMBER'S: 9843888077, 7397162077</div>
            </div>
          </div>
          <table class="offer-tbl">
            <tr><td>Offer Date :</td><td>${fmt(q.offerDate)}</td></tr>
            <tr><td>Offer Valid Upto:</td><td>${fmt(q.offerValidUpto)}</td></tr>
            <tr><td>Qot. Ref No.</td><td>${q.quotationNumber}</td></tr>
          </table>
        </div>

        <div style="text-align:center"><span style="display:inline-block;background:#e2e8f0;font-weight:700;padding:3px 24px">ESTIMATION</span></div>

        <div class="to-block">
          <p><b>TO:</b></p>
          <p><b>${(q.clientName || q.client?.name || '—').toUpperCase()}</b></p>
          <p>${q.clientAddress || ''}</p>
          <p>${q.clientPhone || q.client?.phone || ''}</p>
          <p><b>DEAR SIR,</b></p>
        </div>
        <div class="intro">We thank you for your kind enquiry for <b>${q.enquiryFor || ''}</b> Supply and please find the following quote for your information.</div>

        ${sectionsHtml}

        <div class="grand-total">GRAND TOTAL: ₹${fmt2(grandTotal)}</div>

        <div class="terms">
          <u>Terms &amp; Condition</u>
          <ol>${terms}</ol>
        </div>

        <div class="bank">
          <div class="hd">BANK DETAILS</div>
          <div class="sub">Creative Glass TEC</div>
          <div class="row"><b>Bank:</b> TMB LTD</div>
          <div class="row"><b>Branch:</b> Simmakal, Madurai</div>
          <div class="row"><b>A.C:</b> Type CURRENT A/C</div>
          <div class="row"><b>A.C No.</b> 09353 9843888077</div>
          <div class="row"><b>IFSC:</b> TMBL0000093</div>
        </div>

        <div class="sign">
          <div>Thanking You,</div>
          <b>CREATIVE GLASS TEC</b>
          <div class="signatory">K.MOHAN</div>
        </div>
      </div>
      <script>window.onload=()=>{window.print();}${'<'}/script>
    </body></html>`);
    win.document.close();
  }

  return (
    <Layout>
      {loading && <PageLoader text="Loading customer quotations..." />}
      <div className="quo-root">
        <div className="quo-layout">
          {/* LEFT LIST */}
          <div className="quo-list-col">
            <div className="quo-list-header">
              <span className="quo-list-title">Customer Quotations <span className="quo-count">{filtered.length}</span></span>
            </div>
            <div className="quo-list-search">
              <div className="quo-search-box">
                <FiSearch size={13} className="quo-search-icon" />
                <input placeholder="Search quotations..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
              </div>
            </div>
            <div className="quo-list-items">
              {paginated.length === 0 && <p className="quo-list-empty">No quotations found</p>}
              {paginated.map(q => (
                <div
                  key={q._id}
                  className={`quo-list-item ${selected?._id === q._id ? 'quo-item-selected' : ''}`}
                  onClick={() => setSelected(q)}
                  style={q.status === 'approved' ? { borderLeft: '4px solid #16a34a', background: '#f0fdf4' } : undefined}
                >
                  <div className="quo-item-top">
                    <span className="quo-item-id">{q.quotationNumber}{q.status === 'approved' && <span title="Final approved quotation" style={{ marginLeft: 4 }}>⭐</span>}</span>
                    <StatusBadge status={q.status} />
                  </div>
                  <div className="quo-item-client">{q.clientName || q.client?.name || '—'}</div>
                  {q.project?.title && <div style={{ fontSize: '.72rem', color: '#94a3b8' }}>Project: {q.project.title}</div>}
                  <div className="quo-item-bottom">
                    <span className="quo-item-project">{q.enquiryFor || '—'}</span>
                    <span className="quo-item-amount">{fmtINR(quotationTotal(q))}</span>
                  </div>
                  <div className="quo-item-date">{fmt(q.offerDate)}</div>
                </div>
              ))}
            </div>
            <div className="quo-list-pagination">
              <button className="quo-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`quo-page-btn ${page === p ? 'quo-page-active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="quo-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>

          {/* RIGHT DETAIL */}
          <div className="quo-detail-col">
            <div className="quo-action-bar">
              <button className="quo-btn-primary" onClick={() => { setEditQ(null); setShowModal(true); }}>
                <FiPlus size={13} /> New Customer Quotation
              </button>
              <button className="quo-btn-outline quo-btn-pdf" onClick={exportPDF} disabled={!selected}>
                <FiFileText size={13} color="#dc2626" /> Export PDF
              </button>
            </div>

            {!selected ? (
              <div className="quo-empty-state">
                <FiFileText size={40} color="#cbd5e1" />
                <p>Select a quotation to view details</p>
                <button className="quo-btn-primary" onClick={() => { setEditQ(null); setShowModal(true); }}>
                  <FiPlus size={13} /> New Customer Quotation
                </button>
              </div>
            ) : (
              <div className="quo-detail-body">
                <div className="quo-detail-header">
                  <div className="quo-detail-id-row">
                    <h2 className="quo-detail-id">Quotation # {selected.quotationNumber}</h2>
                    <StatusBadge status={selected.status} />
                    {selected.status === 'approved' && <span title="Final approved quotation">⭐ Final</span>}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '.5rem' }}>
                      {selected.status !== 'sent' && selected.status !== 'approved' && (
                        <button className="quo-btn-sm quo-btn-outline" onClick={() => updateStatus(selected, 'sent')}>Mark Sent</button>
                      )}
                      {selected.status !== 'approved' && (
                        <button className="quo-btn-sm quo-btn-outline" style={{ color: '#16a34a', borderColor: '#bbf7d0' }} onClick={() => updateStatus(selected, 'approved')}>Approve (Final)</button>
                      )}
                      {selected.status !== 'rejected' && (
                        <button className="quo-btn-sm quo-btn-outline" style={{ color: '#dc2626', borderColor: '#fecaca' }} onClick={() => updateStatus(selected, 'rejected')}>Reject</button>
                      )}
                      <button className="quo-btn-sm quo-btn-outline" onClick={() => { setEditQ(selected); setShowModal(true); }}><FiEdit2 size={11}/> Edit</button>
                      <button className="quo-btn-sm quo-btn-outline quo-btn-pdf" onClick={exportPDF}><FiDownload size={11}/> PDF</button>
                      <button className="quo-btn-sm quo-btn-outline" onClick={exportPDF}><FiPrinter size={11}/> Print</button>
                      <button className="quo-btn-sm quo-btn-outline" style={{ color: '#dc2626', borderColor: '#fecaca' }} onClick={() => setConfirmDel(selected)}><FiTrash2 size={11}/> Delete</button>
                    </div>
                  </div>
                  <div className="quo-meta-strip">
                    <div className="quo-meta-block"><span>Customer</span><strong>{selected.clientName || selected.client?.name || '—'}</strong></div>
                    <div className="quo-meta-sep" />
                    <div className="quo-meta-block"><span>Enquiry For</span><strong>{selected.enquiryFor || '—'}</strong></div>
                    <div className="quo-meta-sep" />
                    <div className="quo-meta-block"><span>Offer Date</span><strong>{fmt(selected.offerDate)}</strong></div>
                    <div className="quo-meta-sep" />
                    <div className="quo-meta-block"><span>Valid Upto</span><strong>{fmt(selected.offerValidUpto)}</strong></div>
                    <div className="quo-meta-total">
                      <span>Total Amount</span>
                      <strong className="quo-total-big">{fmtINR(quotationTotal(selected))}</strong>
                    </div>
                  </div>
                </div>

                {(() => {
                  const projId = selected.project?._id || selected.project;
                  const siblings = projId ? list.filter(q => (q.project?._id || q.project) === projId) : [];
                  if (siblings.length < 2) return null;
                  return (
                    <div className="quo-notes-box" style={{ marginTop: '.75rem' }}>
                      <div className="quo-notes-icon">📑</div>
                      <div style={{ flex: 1 }}>
                        <p className="quo-notes-title">Other Quotations for This Project ({siblings.length})</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', marginTop: '.4rem' }}>
                          {siblings.map(q => (
                            <button
                              key={q._id}
                              onClick={() => setSelected(q)}
                              className="quo-btn-sm quo-btn-outline"
                              style={{
                                borderColor: q.status === 'approved' ? '#16a34a' : undefined,
                                color: q.status === 'approved' ? '#16a34a' : undefined,
                                fontWeight: q._id === selected._id ? 700 : 400,
                                background: q._id === selected._id ? '#eff6ff' : undefined,
                              }}
                            >
                              {q.status === 'approved' && '⭐ '}{q.quotationNumber} · {STATUS_CFG[q.status]?.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <style>{`
                  .cq-spec-table, .cq-spec-table th, .cq-spec-table td { border: 1px solid #000; }
                  .cq-spec-table { border-collapse: collapse; }
                  .cq-spec-table th { background: #f8fafc; font-size: .72rem; padding: .35rem; }
                  .cq-spec-table td { padding: .4rem; font-size: .8rem; }
                  .cq-dim-table td { border: none; border-bottom: 1px solid #000; padding: .4rem; }
                  .cq-dim-table tr:last-child td { border-bottom: none; }
                `}</style>
                {(selected.sections || []).map((sec, si) => {
                  const rows = sec.rows?.length ? sec.rows : [{ heightFt: null, widthFt: null, qty: 1 }];
                  const area = sectionArea(sec);
                  const amount = sectionAmount(sec);
                  return (
                    <div key={si} style={{ marginTop: '1rem', border: '1px solid #000', borderRadius: 0 }}>
                      <div style={{ background: '#e2e8f0', fontWeight: 700, padding: '.4rem', fontSize: '.82rem', textAlign: 'center' }}>
                        {(sec.title || 'ESTIMATION').toUpperCase()}
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                      <table className="cq-spec-table" style={{ width: '100%', minWidth: 720 }}>
                        <thead>
                          <tr>
                            <th rowSpan={2} style={{ width: '5%' }}>S.NO</th>
                            <th rowSpan={2} style={{ width: '35%' }}>Descriptions</th>
                            <th colSpan={3}>Dimensions</th>
                            <th rowSpan={2}>Total Area in SQ/FT</th>
                            <th rowSpan={2}>Rate</th>
                            <th rowSpan={2}>Total Value In Rs.</th>
                          </tr>
                          <tr>
                            <th>Height in feet</th><th>Width in feet</th><th>Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ textAlign: 'center', verticalAlign: 'top' }}>{si + 1}</td>
                            <td style={{ verticalAlign: 'top', fontSize: '.78rem' }}>
                              <strong>SPECIFICATIONS:</strong>
                              <ol style={{ margin: '.3rem 0 0', paddingLeft: '1.1rem' }}>
                                {(sec.specifications || []).filter(Boolean).map((s, i) => <li key={i}>{s}</li>)}
                              </ol>
                            </td>
                            <td colSpan={3} style={{ padding: 0 }}>
                              <table className="cq-dim-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                  {rows.map((r, ri) => (
                                    <tr key={ri}>
                                      <td style={{ textAlign: 'center' }}>{r.heightFt ?? '—'}</td>
                                      <td style={{ textAlign: 'center' }}>{r.widthFt ?? '—'}</td>
                                      <td style={{ textAlign: 'center' }}>{r.qty ?? 1}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'top' }}>{fmt2(area)}</td>
                            <td style={{ textAlign: 'center', verticalAlign: 'top' }}>₹{fmt2(sec.rate)}</td>
                            <td style={{ textAlign: 'center', verticalAlign: 'top' }}>₹{fmt2(amount)}</td>
                          </tr>
                          <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
                            <td colSpan={6} style={{ textAlign: 'center' }}>TOTAL</td>
                            <td style={{ textAlign: 'center' }}>₹{fmt2(amount)}</td>
                          </tr>
                        </tbody>
                      </table>
                      </div>
                    </div>
                  );
                })}

                {selected.notes && (
                  <div className="quo-notes-box" style={{ marginTop: '1rem' }}>
                    <div className="quo-notes-icon">📋</div>
                    <div>
                      <p className="quo-notes-title">Terms & Conditions</p>
                      <ol className="quo-notes-list">
                        {selected.notes.split('\n').filter(Boolean).map((n, i) => <li key={i}>{n}</li>)}
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <CustomerQuotationModal
          quotation={editQ}
          onClose={() => setShowModal(false)}
          onSaved={q => {
            setList(prev => editQ ? prev.map(x => x._id === q._id ? q : x) : [q, ...prev]);
            setSelected(q);
            setShowModal(false);
            toast.success(editQ ? 'Quotation updated' : 'Quotation created');
          }}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          title="Delete Quotation"
          message={`Delete "${confirmDel.quotationNumber}"? This cannot be undone.`}
          confirmLabel="Delete"
          confirmColor="#dc2626"
          icon="🗑️"
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => deleteQ(confirmDel._id)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          {...confirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </Layout>
  );
}

/* ── New/Edit Customer Quotation Modal ── */
function CustomerQuotationModal({ quotation, onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]); // flat list of material names from selected project's plan
  const [form, setForm] = useState({
    client: quotation?.client?._id || quotation?.client || '',
    clientName: quotation?.clientName || '',
    clientAddress: quotation?.clientAddress || '',
    clientPhone: quotation?.clientPhone || '',
    project: quotation?.project?._id || quotation?.project || '',
    enquiryFor: quotation?.enquiryFor || '',
    offerDate: quotation?.offerDate ? quotation.offerDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
    offerValidUpto: quotation?.offerValidUpto ? quotation.offerValidUpto.slice(0, 10) : '',
    notes: quotation?.notes || [
      '100% advanced to be paid.',
      'Labour Accommodation & Food at site will be your scope.',
      'Scaffolding, Electricity, Additional Man Power and structural support will be your scope only.',
      'MS Work, Carpenter for Template Making Will be Your Scope.',
      'Secured Storage Place should be provided by you.',
      'Toughened glasses are Tailor and Custom made, once ordered Placed, cannot be or altered.',
      'Quotation valid for 30 days only.',
    ].join('\n'),
  });
  const [sections, setSections] = useState(quotation?.sections?.length
    ? quotation.sections.map(s => ({
        title: s.title || '',
        specifications: s.specifications?.length ? s.specifications : [''],
        rows: s.rows?.length ? s.rows.map(r => ({ heightFt: r.heightFt ?? '', widthFt: r.widthFt ?? '', qty: r.qty ?? 1 })) : [freshRow()],
        rate: s.rate ?? '',
      }))
    : [freshSection()]
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/clients').then(r => setClients(r.data)).catch(() => {});
    Promise.all([
      API.get('/projects'),
      API.get('/material-planning').catch(() => ({ data: [] })),
    ]).then(([projRes, planRes]) => {
      const plannedIds = new Set((planRes.data || []).map(id => String(id)));
      setProjects(projRes.data.filter(p => plannedIds.has(String(p._id))));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.project) { setMaterialOptions([]); return; }
    API.get(`/material-planning/${form.project}`).then(r => {
      const names = [];
      (r.data?.categories || []).forEach(cat => {
        (cat.materials || []).forEach(m => { if (m.materialName) names.push(m.materialName); });
        (cat.subCategories || []).forEach(sub =>
          (sub.materials || []).forEach(m => { if (m.materialName) names.push(m.materialName); })
        );
      });
      setMaterialOptions([...new Set(names)]);
    }).catch(() => setMaterialOptions([]));
  }, [form.project]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function onClientChange(id) {
    const found = clients.find(c => c._id === id);
    set('client', id);
    set('clientName', found ? found.name : '');
    set('clientAddress', found ? (found.address || '') : '');
    set('clientPhone', found ? (found.phone || '') : '');
    set('project', '');
  }

  const projectsForClient = form.client
    ? projects.filter(p => String(p.client?._id || p.client) === String(form.client))
    : projects;

  function addSection() { setSections(p => [...p, freshSection()]); }
  function removeSection(i) { setSections(p => p.filter((_, idx) => idx !== i)); }
  function setSectionField(i, k, v) { setSections(p => p.map((s, idx) => idx === i ? { ...s, [k]: v } : s)); }

  function addSpec(si) { setSections(p => p.map((s, idx) => idx !== si ? s : { ...s, specifications: [...s.specifications, ''] })); }
  function setSpec(si, spi, v) { setSections(p => p.map((s, idx) => idx !== si ? s : { ...s, specifications: s.specifications.map((sp, i) => i === spi ? v : sp) })); }
  function removeSpec(si, spi) { setSections(p => p.map((s, idx) => idx !== si ? s : { ...s, specifications: s.specifications.filter((_, i) => i !== spi) })); }

  function loadSpecsFromPlan(si) {
    if (!materialOptions.length) return toast.warn('Select a project with a material plan first');
    setSections(p => p.map((s, idx) => idx !== si ? s : { ...s, specifications: [...materialOptions] }));
    toast.success(`Loaded ${materialOptions.length} specification(s) from material plan`);
  }

  function addRow(si) { setSections(p => p.map((s, idx) => idx !== si ? s : { ...s, rows: [...s.rows, freshRow()] })); }
  function setRowField(si, ri, k, v) { setSections(p => p.map((s, idx) => idx !== si ? s : { ...s, rows: s.rows.map((r, i) => i === ri ? { ...r, [k]: v } : r) })); }
  function removeRow(si, ri) { setSections(p => p.map((s, idx) => idx !== si ? s : { ...s, rows: s.rows.filter((_, i) => i !== ri) })); }

  async function save() {
    if (!form.clientName.trim()) return toast.error('Customer is required');
    if (!sections.length || sections.some(s => !s.title.trim())) return toast.error('Every section needs a title');
    setSaving(true);
    try {
      const cleanedSections = sections.map(s => ({
        ...s,
        rate: +s.rate || 0,
        specifications: s.specifications.filter(Boolean),
        rows: s.rows.map(r => ({ heightFt: +r.heightFt || 0, widthFt: +r.widthFt || 0, qty: +r.qty || 1 })),
      }));
      const payload = { ...form, sections: cleanedSections, project: form.project || undefined, client: form.client || undefined };
      const r = quotation
        ? await API.put(`/customer-quotations/${quotation._id}`, payload)
        : await API.post('/customer-quotations', payload);
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="quo-overlay">
      <div className="quo-modal" style={{ maxWidth: 820 }} onClick={e => e.stopPropagation()}>
        <div className="quo-modal-header">
          <h3>{quotation ? `Edit ${quotation.quotationNumber}` : 'New Customer Quotation'}</h3>
          <button className="quo-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="quo-modal-body">
          <div className="co-mrow">
            <div className="co-mgroup">
              <label>Customer *</label>
              <select value={form.client} onChange={e => onClientChange(e.target.value)}>
                <option value="">Select Customer...</option>
                {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="co-mgroup">
              <label>Project (for material descriptions)</label>
              <ProjectSearchSelect
                projects={projectsForClient}
                value={form.project}
                objectMode={false}
                onChange={id => set('project', id || '')}
                placeholder={form.client ? 'Select Project…' : 'Select a customer first'}
              />
            </div>
          </div>
          <div className="co-mrow">
            <div className="co-mgroup co-mfull">
              <label>Enquiry For</label>
              <input value={form.enquiryFor} onChange={e => set('enquiryFor', e.target.value)} placeholder="e.g. FIXED WITH OPEN DOOR GLASS PARTITION" />
            </div>
          </div>
          <div className="co-mrow">
            <div className="co-mgroup">
              <label>Offer Date</label>
              <input type="date" value={form.offerDate} onChange={e => set('offerDate', e.target.value)} />
            </div>
            <div className="co-mgroup">
              <label>Offer Valid Upto</label>
              <input type="date" value={form.offerValidUpto} onChange={e => set('offerValidUpto', e.target.value)} />
            </div>
          </div>

          <div className="co-items-editor">
            <div className="co-items-editor-header">
              <span>Sections</span>
              <button className="co-btn-ghost" onClick={addSection}><FiPlus size={11}/> Add Section</button>
            </div>
            {sections.map((sec, si) => (
              <div key={si} className="co-item-row-editor">
                <div className="co-mrow">
                  <div className="co-mgroup co-mfull">
                    <label>Section Title</label>
                    <input value={sec.title} onChange={e => setSectionField(si, 'title', e.target.value)} placeholder="e.g. GLASS PARTITION" />
                  </div>
                  <div className="co-mgroup">
                    <label>Rate (₹/sqft)</label>
                    <input type="number" min="0" value={sec.rate} onChange={e => setSectionField(si, 'rate', e.target.value)} />
                  </div>
                  {sections.length > 1 && <button className="co-remove-item" onClick={() => removeSection(si)}><FiX size={11}/></button>}
                </div>

                <div className="co-mgroup co-mfull">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ marginBottom: 0 }}>Specifications</label>
                    {materialOptions.length > 0 && (
                      <button
                        type="button"
                        className="co-btn-ghost"
                        onClick={() => loadSpecsFromPlan(si)}
                        style={{ color: '#2563eb', borderColor: '#bfdbfe', background: '#eff6ff' }}
                      >
                        <FiPlus size={11}/> Load from Material Plan
                      </button>
                    )}
                  </div>
                  {sec.specifications.map((sp, spi) => (
                    <div key={spi} style={{ display: 'flex', gap: '.4rem', marginBottom: '.3rem' }}>
                      {materialOptions.length > 0 ? (
                        <input
                          list={`materials-${si}`}
                          value={sp}
                          onChange={e => setSpec(si, spi, e.target.value)}
                          placeholder="Type or pick from material plan"
                          style={{ flex: 1 }}
                        />
                      ) : (
                        <input value={sp} onChange={e => setSpec(si, spi, e.target.value)} placeholder="Specification line" style={{ flex: 1 }} />
                      )}
                      <button className="co-remove-item" onClick={() => removeSpec(si, spi)}><FiX size={11}/></button>
                    </div>
                  ))}
                  {materialOptions.length > 0 && (
                    <datalist id={`materials-${si}`}>
                      {materialOptions.map((m, i) => <option key={i} value={m} />)}
                    </datalist>
                  )}
                  <button className="co-btn-ghost" onClick={() => addSpec(si)}><FiPlus size={11}/> Add Spec Line</button>
                </div>

                <div className="co-mgroup co-mfull">
                  <label>Dimension Rows (manual)</label>
                  {sec.rows.map((r, ri) => (
                    <div key={ri} className="co-mrow" style={{ alignItems: 'flex-end' }}>
                      <div className="co-mgroup">
                        <label>Height (ft)</label>
                        <input type="number" min="0" value={r.heightFt} onChange={e => setRowField(si, ri, 'heightFt', e.target.value)} />
                      </div>
                      <div className="co-mgroup">
                        <label>Width (ft)</label>
                        <input type="number" min="0" value={r.widthFt} onChange={e => setRowField(si, ri, 'widthFt', e.target.value)} />
                      </div>
                      <div className="co-mgroup">
                        <label>Qty</label>
                        <input type="number" min="1" value={r.qty} onChange={e => setRowField(si, ri, 'qty', e.target.value)} />
                      </div>
                      {sec.rows.length > 1 && <button className="co-remove-item" onClick={() => removeRow(si, ri)}><FiX size={11}/></button>}
                    </div>
                  ))}
                  <button className="co-btn-ghost" onClick={() => addRow(si)}><FiPlus size={11}/> Add Row</button>
                </div>

                <div style={{ textAlign: 'right', fontSize: '.78rem', fontWeight: 700, color: '#0f172a' }}>
                  Area: {fmt2(sectionArea({ rows: sec.rows }))} sqft &nbsp;|&nbsp; Amount: {fmtINR(sectionAmount({ rows: sec.rows, rate: sec.rate }))}
                </div>
              </div>
            ))}
          </div>

          <div className="co-mgroup">
            <label>Terms & Conditions (one per line)</label>
            <textarea rows={5} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="quo-modal-footer">
          <button className="co-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="co-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : quotation ? 'Save Changes' : 'Create Quotation'}</button>
        </div>
      </div>
    </div>
  );
}
